import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@pgs/shared';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Chrome } from '@/components/layout/Chrome';
import { useApplicantStore } from '@/features/applicant/applicantStore';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

type LoginResponse =
  | { accessToken: string; user: { id: string; email: string; role: 'admin' } }
  | { requires2fa: true; email: string };

export const LoginPage = () => {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const setPendingEmail = useApplicantStore((s) => s.setPendingEmail);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const mutation = useMutation({
    mutationFn: async (input: LoginInput) =>
      (await api.post<LoginResponse>('/auth/login', input)).data,
    onSuccess: (data) => {
      if ('requires2fa' in data) {
        setPendingEmail(data.email);
        navigate('/apply/verify');
        return;
      }
      setSession(data.accessToken, data.user);
      navigate('/admin/submissions');
    },
  });

  return (
    <Chrome>
      <div className="relative min-h-[80vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/hero-bg.jpg')" }}
        />
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background:
              'linear-gradient(to right,rgba(0,229,192,.5) 0%,rgba(0,229,192,.35) 30%,rgba(13,27,62,.7) 70%,rgba(13,27,62,.85) 100%)',
          }}
        />
        <div className="relative z-[2] mx-auto flex max-w-[1400px] flex-col gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-[55%] lg:max-w-[55%]">
            <span className="mb-3.5 inline-block bg-teal px-4 py-2 text-[11px] font-extrabold uppercase tracking-[3px] text-navy">
              Welcome to People Group Services
            </span>
            <h1 className="text-3xl font-black leading-[1.05] tracking-tight text-white md:text-5xl lg:text-[54px]">
              The Simplest
              <br />
              Most Rewarding
              <br />
              Pay Solution
            </h1>
          </div>
          <form
            onSubmit={handleSubmit((v) => mutation.mutate(v))}
            className="w-full max-w-md rounded-sm bg-white/30 p-8 backdrop-blur-md"
          >
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[1.5px] text-white">
              Email
            </label>
            <input
              {...register('email')}
              type="email"
              placeholder="Email address"
              className="mb-1 w-full rounded-sm border-[1.5px] border-white/30 bg-white/30 px-3.5 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-teal focus:ring-2 focus:ring-teal/20"
            />
            {errors.email && <p className="mb-2 text-[11px] text-red-200">{errors.email.message}</p>}
            <label className="mt-3 mb-1.5 block text-[10px] font-bold uppercase tracking-[1.5px] text-white">
              Password
            </label>
            <input
              {...register('password')}
              type="password"
              placeholder="Password"
              className="mb-1 w-full rounded-sm border-[1.5px] border-white/30 bg-white/30 px-3.5 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-teal focus:ring-2 focus:ring-teal/20"
            />
            {errors.password && (
              <p className="mb-2 text-[11px] text-red-200">{errors.password.message}</p>
            )}
            {mutation.isError && (
              <p className="mb-2 text-[11px] text-red-200">Invalid credentials</p>
            )}
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="mb-3 block w-full text-right text-[10px] uppercase tracking-wider text-white/80 hover:text-teal"
            >
              Forgot Password?
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-navy">
              {mutation.isPending ? 'Signing in…' : 'Sign In'}
            </button>

            <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider text-white/60">
              <div className="h-px flex-1 bg-white/30" />
              <span>New here?</span>
              <div className="h-px flex-1 bg-white/30" />
            </div>

            <button
              type="button"
              onClick={() => navigate('/apply/signup')}
              className="block w-full rounded-sm bg-teal py-3 text-sm font-extrabold uppercase tracking-[2px] text-navy transition hover:bg-white"
            >
              Register Now
            </button>
          </form>
        </div>
      </div>
    </Chrome>
  );
};
